import httpos from "./httpos";

/**
 * Job tracker pins: where the company is with a property.
 *
 * In job order, matching the backend's PropertyPin::STATUSES (and the admin
 * badges in CompanyPins::BADGE_STYLES). The standard pin colours are blue,
 * yellow, red and green. `textColor` is for text on the colour; `ink` is the
 * colour as text on white, darker for yellow so it stays readable.
 */
export const PIN_STATUSES = [
  { key: "contacted", label: "Contacted", color: "#2563EB", textColor: "#fff", ink: "#2563EB" },
  { key: "inspected", label: "Inspected", color: "#EAB308", textColor: "#1F2937", ink: "#A16207" },
  { key: "contracted", label: "Contracted", color: "#DC2626", textColor: "#fff", ink: "#DC2626" },
  { key: "built", label: "Built", color: "#16A34A", textColor: "#fff", ink: "#16A34A" },
];

const FALLBACK_COLOR = "#6B7280";

export function pinStatus(key) {
  return PIN_STATUSES.find((status) => status.key === key) || null;
}

export function pinColor(key) {
  return pinStatus(key)?.color || FALLBACK_COLOR;
}

/** A pin's person as the API sends it ({ id, name }); a removed seat has no name. */
export function pinPersonName(person) {
  return person?.name || "a removed user";
}

/** Pins the map can draw (with a point). */
export function pinHasPoint(pin) {
  return Number.isFinite(pin?.latitude) && Number.isFinite(pin?.longitude);
}

/**
 * Who placed the pins, for the map's "Placed by" choice: you first, then the
 * account owner, then everyone else by pin count. Pins belong to the company, so
 * every seat sees every placer.
 *
 * @returns {{ id: number, label: string, count: number }[]}
 */
export function pinPlacers(pins, { viewerId = null, ownerId = null } = {}) {
  const byId = new Map();
  (pins || []).forEach((pin) => {
    const id = pin?.created_by?.id;
    if (id === null || id === undefined) return;
    const entry = byId.get(id) || { id, name: pinPersonName(pin.created_by), count: 0 };
    entry.count += 1;
    byId.set(id, entry);
  });

  const rank = (entry) => (entry.id === viewerId ? 0 : entry.id === ownerId ? 1 : 2);
  return [...byId.values()]
    .map((entry) => ({
      id: entry.id,
      count: entry.count,
      label: entry.id === viewerId ? "You" : entry.id === ownerId ? `${entry.name} (owner)` : entry.name,
    }))
    .sort((a, b) => rank(a) - rank(b) || b.count - a.count || a.label.localeCompare(b.label));
}

/** What users read for a pin: this build's label, else the server's, e.g. for a new status. */
export function pinLabel(pin) {
  return pinStatus(pin?.status)?.label || pin?.status_label || "";
}

/**
 * Zoomed out, nearby pins merge into one numbered bubble; past this zoom every
 * pin is drawn on its own. 11 is roughly a whole city on screen.
 */
export const PIN_CLUSTER_MAX_ZOOM = 11;
export const PIN_CLUSTER_RADIUS = 40;
/** Neutral, so a bubble never reads as one status. */
export const PIN_CLUSTER_COLOR = "#1F2937";

/** Mapbox expression colouring each pin by its status. */
export const PIN_COLOR_EXPRESSION = [
  "match",
  ["get", "status"],
  ...PIN_STATUSES.flatMap((status) => [status.key, status.color]),
  FALLBACK_COLOR,
];

/** Pins with a point, as a FeatureCollection; pins without one stay off the map. */
export function pinsToGeoJson(pins) {
  return {
    type: "FeatureCollection",
    features: (pins || [])
      .filter((pin) => Number.isFinite(pin?.latitude) && Number.isFinite(pin?.longitude))
      .map((pin) => ({
        type: "Feature",
        id: pin.id,
        geometry: { type: "Point", coordinates: [pin.longitude, pin.latitude] },
        properties: { id: pin.id, status: pin.status },
      })),
  };
}

export async function fetchPropertyPins() {
  const { data } = await httpos.get("property-pins");
  return data;
}

/** Pin a property (by the encoded id search results carry), or move its pin. */
export async function savePropertyPin(propertyId, status) {
  const { data } = await httpos.post("property-pins", { property_id: propertyId, status });
  return data?.pin;
}

export async function deletePropertyPin(pinId) {
  await httpos.delete(`property-pins/${pinId}`);
}

/** The server's message for a failed pin request, or a generic one. */
export function pinErrorMessage(error) {
  return error?.response?.data?.message || "Could not update the pin. Please try again.";
}
